import React, { useCallback, useEffect, useState } from "react";
import AdminSidebar from "../../sidebar/admin_sidebar";
import AdminHeader from "../../header/admin_header";
import RatesCard from "./RatesCard";
import DiscountForm from "./DiscountForm";
import DiscountTable from "./DiscountTable";
import {
  createDiscount,
  deleteDiscount,
  fetchCompanies,
  fetchCreditConfig,
  fetchDiscounts,
  saveCreditConfig,
  updateDiscount,
} from "./aiCreditsApi";
import "./AiCredits.css";

/**
 * Super-admin screen for AI credit pricing.
 *
 * @param {object} props - Component props, supplying the router location.
 * @returns {JSX.Element} The AI credits page.
 *
 * @remarks
 * Rates and discounts sit on one screen because they are the two halves of the
 * same commercial decision: markup is what usage costs, a discount is what
 * buying costs. Splitting them across screens invites changing one without
 * seeing the other.
 */
const AiCredits = (props) => {
  const [config, setConfig] = useState(null);
  const [discounts, setDiscounts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingRates, setSavingRates] = useState(false);
  const [savingDiscount, setSavingDiscount] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);

    const [nextConfig, nextDiscounts, nextCompanies] = await Promise.all([
      fetchCreditConfig(),
      fetchDiscounts(),
      fetchCompanies(),
    ]);

    setConfig(nextConfig);
    setDiscounts(nextDiscounts);
    setCompanies(nextCompanies);
    setError(
      nextConfig
        ? null
        : "Could not read the credit configuration. The ledger migration may not have been run on this environment."
    );
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSaveRates = async (body) => {
    setSavingRates(true);
    const saved = await saveCreditConfig(body);
    if (saved) {
      setConfig(saved);
    }
    setSavingRates(false);
  };

  const handleSubmitDiscount = async (body) => {
    setSavingDiscount(true);

    const saved = editing
      ? await updateDiscount(editing.id, body)
      : await createDiscount(body);

    if (saved) {
      setEditing(null);
      setDiscounts(await fetchDiscounts());
    }

    setSavingDiscount(false);
  };

  const handleDeleteDiscount = async (discount) => {
    setRemovingId(discount.id);

    if (await deleteDiscount(discount.id)) {
      if (editing && editing.id === discount.id) {
        setEditing(null);
      }
      setDiscounts(await fetchDiscounts());
    }

    setRemovingId(null);
  };

  return (
    <div>
      <AdminSidebar dataFromParent={props?.location?.pathname || "/ai_credits"} />
      <AdminHeader />

      <div className="main_wrapper">
        <div className="inner_wraapper">
          <div className="aicr-page">
          <div className="aicr-head">
            <div>
              <div className="aicr-eyebrow">Billing</div>
              <h1>AI Credits</h1>
              <p className="aicr-tagline">
                AI features bill on what they cost to run, plus a markup.
                Customers buy credits up front and spend them as they use AI.
              </p>
            </div>
          </div>

          {loading && <div className="aicr-card aicr-muted">Loading…</div>}

          {!loading && error && (
            <div className="aicr-card aicr-error">
              <p>{error}</p>
              <button
                type="button"
                className="aicr-btn aicr-btn-secondary"
                onClick={load}
              >
                Try again
              </button>
            </div>
          )}

          {!loading && config && (
            <>
              <RatesCard
                key={`${config.id}`}
                config={config}
                onSave={handleSaveRates}
                saving={savingRates}
              />

              <DiscountForm
                key={editing ? `edit-${editing.id}` : "new"}
                editing={editing}
                companies={companies}
                onSubmit={handleSubmitDiscount}
                onCancel={() => setEditing(null)}
                saving={savingDiscount}
              />

              <DiscountTable
                discounts={discounts}
                onEdit={setEditing}
                onDelete={handleDeleteDiscount}
                busyId={removingId}
              />
            </>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiCredits;
